// Importation des dépendances React
import React, { useState, useMemo, useEffect, memo } from "react";

// Importation des icônes utilisées pour les fichiers et les boutons
import folderIcon from "../assets/folder.png";
import fileIcon from "../assets/file.png";
import pdfIcon from "../assets/pdf.png";
import xlsxIcon from "../assets/xlsx.png";
import docIcon from "../assets/doc.png";
import docxIcon from "../assets/docx.png";
import htmlIcon from "../assets/html.png";
import sqlIcon from "../assets/sql.png";
// import zoomInIcon from "../assets/zoom.png";
// import zoomOutIcon from "../assets/dezoom.png";
import viewSwitchIcon from "../assets/view-switch.png";
import addfileIcon from "../assets/addfile.png";
import addfolderIcon from "../assets/addfolder.png";
import threepointsIcon from "../assets/threepoints.png";

// Importation des pop-ups de gestion des fichiers/dossiers
import PopUpDownload from "./PopUpDownload";
import PopUpDeleteObject from "./PopUpDeleteObject";
import PopUpAddFile from "./PopUpAddFile";
import PopUpAddFolder from "./PopUpAddFolder";
import PopUpMoveObject from "./PopUpMoveObject";
import PopUpCopyObject from "./PopUpCopyObject";
import PopUpRenameObject from "./PopUpRenameObject";

// Importation des utilitaires (cookies, logs)
import { getCookie, setCookie, logAction } from "./log.js";

import { /*ZoomIn, ZoomOut, */ LongArrowDownRight, PasteClipboard, Page, Folder, LayoutRight, MoreVert, Copy, PathArrow, InputField, Trash, LogOut, Bank, PagePlus, FolderPlus, Home} from 'iconoir-react';
import { /*ZoomIn, ZoomOut, */ DownloadCircle} from 'iconoir-react';

// Importation du CSS spécifique au composant
import "../css/InfoFolder.css";

/**
 * Objet associant les extensions de fichiers aux icônes correspondantes
 */
const fileIcons = { pdf: pdfIcon, xlsx: xlsxIcon, doc: docIcon, docx: docxIcon, html: htmlIcon, sql: sqlIcon, txt: fileIcon };

/**
 * Composant principal gérant l'affichage et les interactions avec le système de fichiers.
 *
 * Props:
 * - tree: Arborescence (liste récursive) du dossier courant
 * - baseFolder: Nom du dossier racine (ex: "Documents")
 * - currentPath: Chemin courant sous forme de tableau (ex: ["Documents", "Projets"])
 * - dossier_id: ID unique du dossier courant
 * - navigateToPath: Fonction pour changer de dossier (mise à jour du chemin)
 * - authorisation: Niveau d'autorisation utilisateur (ex: 2 pour admin)
 * - navigate: Fonction de navigation globale (ex: redirections)
 * - setIsLegalPopupOpen: Fonction pour ouvrir la popup des mentions légales
 * - handleLogout: Fonction pour la déconnexion utilisateur
 *  */
const InfoFolder = ({
                        tree, // Arborescence du dossier courant
                        baseFolder, // Nom du dossier racine
                        currentPath, // Chemin courant
                        dossier_id, // ID du dossier courant
                        navigateToPath, // Fonction pour naviguer entre les dossiers
                        authorisation, // Niveau d'autorisation de l'utilisateur
                        navigate, // Fonction de navigation globale
                        setIsLegalPopupOpen, // Ouvre la popup des mentions légales
                        handleLogout // Déconnexion utilisateur
                    }) => {
    // Etats du composant
    const [zoomLevel, setZoomLevel] = useState(() => parseFloat(getCookie("zoomLevel")) || 1.0); // Niveau de zoom initial
    const [viewMode, setViewMode] = useState(() => getCookie("viewMode") || "list"); // Mode d'affichage initial : liste ou grille
    const [sortBy, setSortBy] = useState(() => getCookie("sortBy") || "type"); // Critère de tri initial
    const [legal, setLegal] = useState(() => getCookie("legal") || ""); // Critère de tri initial
    const [searchQuery, setSearchQuery] = useState(""); // Requête de recherche
    const [animationStep, setAnimationStep] = useState("idle"); // Animation en cours (transition)
    const [selectedObject, setSelectedObject] = useState(null); // Objet sélectionné pour téléchargement
    const [modals, setModals] = useState({ delete: null, move: null, copy: null, rename: null }); // Gestion des popups
    const [showAddFilePopup, setShowAddFilePopup] = useState(false); // Popup ajout de fichier
    const [showAddFolderPopup, setShowAddFolderPopup] = useState(false); // Popup ajout de dossier
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, file: null }); // Menu contextuel

    // Sauvegarde des préférences utilisateur dans les cookies
    useEffect(() => { setCookie("zoomLevel", zoomLevel); }, [zoomLevel]);
    useEffect(() => { setCookie("viewMode", viewMode); }, [viewMode]);
    useEffect(() => { setCookie("sortBy", sortBy); }, [sortBy]);

    /**
     * Fonction récursive qui "aplatit" l'arborescence des dossiers/fichiers
     * en une liste simple, en ajoutant des informations utiles (id, extension, etc.).
     *
     * @param {Array} nodes - Liste de nœuds (dossiers/fichiers)
     * @returns {Array} Liste plate des objets avec métadonnées
     */
    const flattenTree = (nodes) => nodes.flatMap(node => {
        const ext = node.type === "F" ? node.name.split(".").pop().toLowerCase() : null;
        const item = { id: dossier_id, name: node.name || "-", type: node.type, path: node.path, datemaj: node.datemaj || "-", rawType: node.type === "F" ? ext : undefined };
        return node.type === "D" && node.children?.length ? [item, ...flattenTree(node.children)] : [item];
    });

    // Liste complète des objets (fichiers/dossiers)
    const allObjects = useMemo(() => flattenTree(tree || []), [tree, currentPath.join("/")]);

    /**
     * Filtre et trie la liste des fichiers/dossiers selon la recherche et le critère choisi.
     * Ne prend pas en compte le premier élément qui correspond au dossier racine.
     */
    const filteredItems = useMemo(() => {
        let objects = allObjects.slice(1);
        if (searchQuery.trim()) objects = objects.filter(obj => obj.name.toLowerCase().includes(searchQuery.toLowerCase()));

        const sorters = {
            name: (a, b) => a.name.localeCompare(b.name),
            date: (a, b) => (b.datemaj || "").localeCompare(a.datemaj || ""),
            extension: (a, b) => (a.rawType || "").localeCompare(b.rawType || "") || a.name.localeCompare(b.name),
            type: (a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type.localeCompare(b.type))
        };

        return objects.sort(sorters[sortBy]);
    }, [allObjects, searchQuery, sortBy]);

    /**
     * Fonction qui gère l'animation lors de la navigation vers un nouveau dossier.
     * Appelle navigateToPath et log l'action.
     *
     * @param {Array} newPath - Nouveau chemin vers lequel naviguer
     */
    const animateNavigate = async (newPath) => {
        setAnimationStep("out");
        setTimeout(async () => {
            await navigateToPath(newPath);
            logAction({ action: "goToFolder", dossierId: dossier_id, utilisateurId: getCookie("authEmail"), path: newPath.join("\\") });
            setAnimationStep("in");
            setTimeout(() => setAnimationStep("idle"), 250);
        }, 250);
    };

    // Gestion du clic sur un fichier ou dossier
    const handleClick = async (e, obj) => {
        e.stopPropagation();
        if (obj.type === "D") {
            setContextMenu({ visible: false, x: 0, y: 0, file: null });
            await animateNavigate([...currentPath, obj.name]);
        } else {
            const menuWidth = 200;
            let x = e.pageX;
            if (x + menuWidth > window.innerWidth) x -= menuWidth;
            setContextMenu({ visible: true, x, y: e.pageY, file: obj });
        }
    };

    // Ouvre le menu contextuel sur le bouton "plus"
    const handleMoreButtonClick = (e, item) => {
        const rect = e.currentTarget.getBoundingClientRect();
        let x = rect.left;
        if (x + 200 > window.innerWidth) x = rect.right - 200;
        setContextMenu({ visible: true, x, y: rect.bottom, file: item });
    };

    // Ferme une popup et rafraîchit le dossier courant
    const closePopupAndRefresh = (key, value = null) => {
      // console.log("onclose");
      setSelectedObject();
      setShowAddFilePopup(false)
      setShowAddFolderPopup(false)
      setModals(prev => ({ ...prev, [key]: value }));
      navigateToPath(currentPath);
    };

    const openLegal  = () => {
      setLegal(getCookie("legal")||"");
      if(legal){
        window.open(legal, '_blank');
      }else{
        setIsLegalPopupOpen(true);
      }
    }


    // Formatte la date affichée
    const formatDate = (raw) => raw?.length === 14 ? `${raw.slice(6, 8)}/${raw.slice(4, 6)}/${raw.slice(0, 4)} à ${raw.slice(8, 10)}:${raw.slice(10, 12)}` : "-";

    // Retourne l'icône en fonction du type de fichier
    const getIcon = (item) => item.type === "D" ? folderIcon : fileIcons[item.rawType] || fileIcon;

    /**
     * Composant affichant une carte représentant un fichier ou dossier
     * Utilise React.memo pour éviter les rerenders inutiles.
     *
     * Props:
     * - item: objet fichier/dossier à afficher
     * - onClick: fonction appelée au clic sur la carte
     * - onMoreClick: fonction appelée au clic sur le bouton "trois points"
     */
    const FileCard = memo(({ item, onClick, onMoreClick }) => {
      // console.log(item);
      
        const name = item.type === "D" ? item.name : item.name.replace(/\.[^/.]+$/, "");
        const date = formatDate(item.datemaj);
        return viewMode === "grid" ? (
            <div className="file-card folder-card md:w-1/4 lg:w-1/5" onClick={(e) => onClick(e, item)}>
                {/* <img src={getIcon(item)} alt="" className="file-icon" /> */}
              <div className="grid-folder-header">
                {item.type === "D" ? (
                  // <img src={folderIcon} alt="dossier" className="file-icon" />
                  <div className={`file-icon fi fi-ogg fi-size-xs- `}><div className="fi-content"><Folder width="14" height="14"/></div></div>
                ) : (
                  <div className={`file-icon fi fi-${item.rawType}`}><div className="fi-content">{item.rawType}</div></div>
                )}
                {/* <img src={threepointsIcon} alt="" className="threepoints-icon-grid" onClick={(e) => { e.stopPropagation(); onMoreClick(e, item); }} /> */}
                <MoreVert width="20" height="20" onClick={(e) => { e.stopPropagation(); onMoreClick(e, item); }}/>
              </div>                
              <div className="file-name" title={item.name}><div className="flex items-center">
                {item.type === "D" && ( <LongArrowDownRight width="14" height="14"/>)}{name}
                </div></div>
              <div className="file-info"><span>{date}</span></div>
            </div>
        ) : (
            <div className="file-card-list px-3 py-2">
                <div className="file-card-col file-card-main" onClick={(e) => onClick(e, item)} style={{ cursor: "pointer" }}>
                    {/* <img src={getIcon(item)} alt="" className="file-icon" /> */}
                    {/* <div className={`file-icon fi fi-${item.rawType} fi-size-xs- `}><div className="fi-content">{item.rawType}</div></div> */}
                {item.type === "D" ? (
                  // <img src={folderIcon} alt="dossier" className="file-icon" />
                  <div className={`file-icon fi fi-ogg fi-size-xs- `}><div className="fi-content"><Folder width="14" height="14"/></div></div>
                ) : (
                  <div className={`file-icon fi fi-${item.rawType}`}><div className="fi-content">{item.rawType}</div></div>
                )}                    

              <div className="file-name -flex-col md:flex-row" title={item.name}>
                <div className="flex items-center">{item.type === "D" && ( <LongArrowDownRight width="14" height="14"/>)}{name}</div>
                <div className="file-info-list text-sm text-gray-500">{date}</div>
              </div>

                </div>
                <div className="file-card-col file-card-type">{item.rawType?.toUpperCase() || (item.type === "D" ? "Dossier" : "Fichier")}</div>
                {/* <div className="file-card-col file-card-date hidden md:block">{date}</div> */}
                <div className="file-card-col file-card-actions">
                    {/* <img src={threepointsIcon} alt="" className="threepoints-icon-list" onClick={(e) => { e.stopPropagation(); onMoreClick(e, item); }} /> */}
                    {/* <MoreVert width="20" height="20" onClick={(e) => { e.stopPropagation(); onMoreClick(e, item); }}/> */}
                  {/* <div class="dropdown">
                    <ul class="menu dropdown-content bg-base-100 rounded-box z-1 w-52 p-2 shadow-sm">
                      <li><a>Item 1</a></li>
                      <li><a>Item 2</a></li>
                    </ul>
                  </div> */}

                  {/* <div className="dropdown dropdown-end" >
                    <div tabIndex="0" role="button" className="btn btn-circle btn-ghost btn-xs"><MoreVert /></div>
                    <ul tabIndex="1" className="dropdown-content menu bg-base-100 border rounded z-10 w-52 p-2 shadow">
                      <div className='delete-tache' ><li><p><Trash/> Supprimer </p></li></div>
                    </ul>
                  </div> */}


                  <div className="dropdown dropdown-end">  
                    <div tabIndex="0" role="button" className="m-1"><MoreVert width="20" height="20"/></div>
                    <ul tabIndex="1" className="dropdown-content menu- bg-base-200 rounded-box z-100 w-64 p-2 shadow-sm">
                        <div className="flex p-2 cursor-pointer hover:bg-gray-200" onClick={() => { setSelectedObject(item); setContextMenu({ ...contextMenu, visible: false })} }><DownloadCircle className="z-10 mr-2 text-teal-600" width={20} height={20}/> Télécharger</div>
                        {authorisation == 2 && (
                            <>
                              <div className="flex p-2 cursor-pointer hover:bg-gray-200"  onClick={() => { setModals(prev => ({ ...prev, rename: item })); setContextMenu({ ...contextMenu, visible: false });}}><InputField className="mr-2" width={20} height={20}/> Renommer</div>
                              <div className="flex p-2 cursor-pointer hover:bg-gray-200"  onClick={() => { setModals(prev => ({ ...prev, move: item })); setContextMenu({ ...contextMenu, visible: false }); }}><PathArrow className="mr-2" width={20} height={20}/> Déplacer</div>
                              <div className="flex p-2 cursor-pointer hover:bg-gray-200"  onClick={() => { setModals(prev => ({ ...prev, copy: item })); setContextMenu({ ...contextMenu, visible: false }); }}><Copy className="mr-2" width="20" height="20"/> Copier</div>
                              <div className="flex p-2 cursor-pointer hover:bg-gray-200"  onClick={() => { setModals(prev => ({ ...prev, delete: item })); setContextMenu({ ...contextMenu, visible: false });}}><Trash className="mr-2 text-red-600" width="20" height="20"/> Supprimer</div>
                            </>
                        )}
                    </ul>
                  </div>                  
                </div>
            </div>
        );
    });

    return (
        <div className="info-folder px-3" onClick={() => contextMenu.visible && setContextMenu(prev => ({ ...prev, visible: false }))}>
            {/* Barre supérieure avec recherche, zoom et options */}
            <div className="p-2! top-controls ">
                <p className="p-3- font-bold text-sm sm:hidden">{baseFolder}</p>
                <input className="search-bar" placeholder="Rechercher un fichier ou dossier..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                <div className="zoom-controls">
                    {/* <div className="zoom-controls-top">
                        <label className="select-sort-label" htmlFor="sortBy">Trier par :</label>
                        <select className="select-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="type">Type</option>
                            <option value="name">Nom</option>
                            <option value="date">Date</option>
                            <option value="extension">Extension</option>
                        </select>
                    </div> */}
                    <div className="zoom-controls-bottom">
                        {authorisation == 2 && (
                            <>
                            {/* <img src={addfileIcon} alt="Ajouter fichier" /> */}
                            {/* <img src={addfolderIcon} alt="Ajouter dossier" /> */}
                                <button onClick={() => setShowAddFilePopup(true)} title="Ajouter un fichier"><PagePlus color="red-" height={22} width={22} alt="Ajouter fichier"/></button>
                                <button onClick={() => setShowAddFolderPopup(true)} title="Ajouter un dossier"><FolderPlus color="red-" height={22} width={22} alt="Ajouter dossier"/></button>
                            </>
                        )}
                        {/* <button onClick={() => setZoomLevel(z => Math.min(2, z + 0.1))} title="Zoom avant"><ZoomIn color="red-" height={24} width={24} /></button>
                        <button onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.1))} title="Zoom arrière"><ZoomOut color="red-" height={24} width={24} /></button> */}
                        <button onClick={() => setViewMode(v => (v === "grid" ? "list" : "grid"))} title="Changer la vue"><LayoutRight color="red-" height={22} width={22}/></button>
                        
                    </div>
                </div>
            </div>

            {/* Fil d’Ariane (breadcrumb) */}
            {/* <div className="breadcrumb">
                <span style={{ color: "#666", fontWeight: "bold" }}>📁 {baseFolder}</span>
                {currentPath.map((seg, i) => (
                    <span key={i}> / <span onClick={() => i !== currentPath.length - 1 && animateNavigate(currentPath.slice(0, i + 1))} style={{ cursor: i !== currentPath.length - 1 ? "pointer" : "default", color: i !== currentPath.length - 1 ? "#0077cc" : "#333", fontWeight: i === currentPath.length - 1 ? "bold" : "normal" }}>{seg}</span></span>
                ))}
            </div> */}

            {/* <div className="breadcrumbs text-sm mb-6 p-3 marquee- flex truncate">
              <span className="sm:hidden flex" onClick={() => currentPath.length > 0 && animateNavigate([])} style={{ cursor: currentPath.length > 0 ? "pointer" : "default" }}><Home width={20} height={20}/></span>
              <ul>
                <li className="hidden sm:flex" onClick={() => currentPath.length > 0 && animateNavigate([])} style={{ cursor: currentPath.length > 0 ? "pointer" : "default" }}> {baseFolder}</li>
                {currentPath.map((seg, i) => (
                    seg ? <li key={i}> <span onClick={() => i !== currentPath.length - 1 && animateNavigate(currentPath.slice(0, i + 1))} style={{ cursor: i !== currentPath.length - 1 ? "pointer" : "default", fontWeight: i === currentPath.length - 1 ? "bold" : "normal" }}>{seg}</span></li> : null
                ))}
              </ul>
            </div> */}


            <div className="breadcrumbs text-sm mb-6 p-3 marquee- flex truncate">
              <span className="sm:hidden flex" ><Home width={20} height={20}/></span>
              <ul>
                <li className="hidden sm:flex"> {baseFolder}</li>
                {currentPath.map((seg, i) => (
                    seg ? <li key={i}> <span onClick={() => i !== currentPath.length - 1 && animateNavigate(currentPath.slice(0, i + 1))} style={{ cursor: i !== currentPath.length - 1 ? "pointer" : "default", fontWeight: i === currentPath.length - 1 ? "bold" : "normal" }}>{seg}</span></li> : null
                ))}
              </ul>
            </div>

            {/* Zone d'affichage des fichiers et dossiers */}
            <div className={animationStep === "out" ? "fade-out-left" : animationStep === "in" ? "fade-in-right" : ""}>
                <div className="folder-content-wrapper mb-16">
                    <div className={`card-container ${viewMode} zoomed pb-8`} >
                    {/* <div className={`card-container`} > */}
                        {filteredItems.length === 0 ? <div className="empty-folder-message">Ce dossier est vide</div> : filteredItems.map(item => <FileCard key={item.id + item.name} item={item} onClick={handleClick} onMoreClick={handleMoreButtonClick} />)}
                    </div>
                </div>
            </div>

            {/* Menu contextuel des fichiers */}
            {contextMenu.visible && contextMenu.file && (
                <div className="context-menu text-sm p-2 absolute w-[150px] dropdown-content bg-base-200 rounded-box z-100 w-64 p-2 shadow-sm" style={{ top: contextMenu.y, left: contextMenu.x }}>
                    {/* <ul style={{ listStyle: "none", margin: 0, padding: "10px 0" }}> */}
                        <div className="flex p-2 cursor-pointer hover:bg-gray-200" onClick={() => { setSelectedObject(contextMenu.file); setContextMenu({ ...contextMenu, visible: false }); }}><DownloadCircle className="z-10 mr-2 text-teal-600" width={20} height={20}/> Télécharger</div>
                        {authorisation == 2 && (
                            <>
                                <div className="flex p-2 cursor-pointer hover:bg-gray-200" onClick={() => { setModals(prev => ({ ...prev, rename: contextMenu.file })); setContextMenu({ ...contextMenu, visible: false }); }}><InputField className="mr-2" width={20} height={20}/> Renommer</div>
                                <div className="flex p-2 cursor-pointer hover:bg-gray-200" onClick={() => { setModals(prev => ({ ...prev, move: contextMenu.file })); setContextMenu({ ...contextMenu, visible: false }); }}><PathArrow className="mr-2" width={20} height={20}/> Déplacer</div>
                                <div className="flex p-2 cursor-pointer hover:bg-gray-200" onClick={() => { setModals(prev => ({ ...prev, copy: contextMenu.file })); setContextMenu({ ...contextMenu, visible: false }); }}><Copy className="mr-2" width="20" height="20"/> Copier</div>
                                <div className="flex p-2 cursor-pointer hover:bg-gray-200" onClick={() => { setModals(prev => ({ ...prev, delete: contextMenu.file })); setContextMenu({ ...contextMenu, visible: false }); }}><Trash className="mr-2 text-red-600" width="20" height="20"/> Supprimer</div>
                            </>
                        )}
                    {/* </ul> */}
                </div>
            )}

            {/* Navigation mobile */}
            <div className="mobile-nav-links">
                {authorisation == 2 && <button onClick={() => navigate("/log")} className="hidden btn mobile-nav-item-logs"><PasteClipboard width="18" height="18" className="-mt-1"/> Logs</button>}
                <button onClick={() => openLegal()} className="btn mobile-nav-item-legal"><Bank width="18" height="18" className="-mt-1"/> Mentions Légales</button>
                <button onClick={handleLogout} className="btn mobile-nav-item-deconnect text-red-600"><LogOut width="18" height="18" className=""/> Déconnexion</button>
            </div>

            {/* Popups de gestion */}
            {selectedObject && <PopUpDownload object={selectedObject} isFolder={selectedObject.type === "D"} pathArray={currentPath} onClose={() => closePopupAndRefresh("selectedObject")} />}
            {showAddFilePopup && <PopUpAddFile object={{ name: "", pathArray: currentPath, type: "F", id: dossier_id }} currentPath={currentPath} navigateToPath={navigateToPath} onClose={() => closePopupAndRefresh("showAddFilePopup")} />}
            {showAddFolderPopup && <PopUpAddFolder object={{ name: "", pathArray: currentPath, type: "D", id: dossier_id }} currentPath={currentPath} navigateToPath={navigateToPath} onClose={() => closePopupAndRefresh("showAddFolderPopup")} />}
            {modals.delete && <PopUpDeleteObject object={modals.delete} currentPath={currentPath} navigateToPath={navigateToPath} onClose={() => closePopupAndRefresh("delete")} />}
            {modals.move && <PopUpMoveObject object={modals.move} currentPath={currentPath} navigateToPath={navigateToPath} rootFolder={currentPath[0]} onClose={() => closePopupAndRefresh("move")} />}
            {modals.copy && <PopUpCopyObject object={modals.copy} currentPath={currentPath} navigateToPath={navigateToPath} rootFolder={currentPath[0]} onClose={() => closePopupAndRefresh("copy")} />}
            {modals.rename && <PopUpRenameObject object={modals.rename} currentPath={currentPath} navigateToPath={navigateToPath} onClose={() => closePopupAndRefresh("rename")} />}
        </div>
    );
};

export default InfoFolder;
